import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useAppContext } from "../Contexts/AppContext";
import Constants from "../Utilities/Constants";

// UI Constants
const UNIT_SELECTOR_STYLES = {
    container: { width: '110px', height: '30px' },
    activeButton: { width: '60%' },
    inactiveButton: { width: '40%' }
};

const BUTTON_STYLES = {
    notes: {
        background: 'white',
        border: 'none',
        cursor: 'pointer',
        lineHeight: '18px'
    },
    remove: {
        background: 'white',
        border: 'none',
        cursor: 'pointer',
        color: '#dc3545',
        lineHeight: '18px'
    }
};

const ICON_SIZE = '18px';
const NOTES_FONT_SIZE = '12px';

/**
 * ProductActions Component
 * 
 * Handles product quantity controls, unit switching, and notes for products.
 * Works both for products in cart and products not yet added to cart.
 * 
 * Props:
 * - product: Enriched product object with units array
 * - showNotes: Whether to show notes input field
 * - className: CSS classes for layout control  
 * - onUnitChange: Callback when user switches between units (unitId)
 */
const ProductActions = React.forwardRef(({
        product,
        showNotes = false,
        className = "",
        onUnitChange = null
    }, ref) => {

    const { cart, setCart, cartId } = useAppContext();
    const [showNotesInput, setShowNotesInput] = useState(false);

    // Always-current cart reference so async callbacks don't capture a stale closure
    const cartRef = useRef(cart);
    useEffect(() => { cartRef.current = cart; }, [cart]);
   
    // Early return for invalid product data
    if (!product || !product.units || !product.units.length) {
        return <div>Loading...</div>;
    }

    // Get current cart info for this product
    const cartitem = cart.find((p) => p.productId === product.id);
    const currentQuantity = cartitem ? cartitem.itemQtty : 0;
    const currentNotes = cartitem ? cartitem.itemNotes : '';

    // Get the active unit - prioritize cart item's unit if product is in cart
    let activeUnit, activeUnitId;
    if (cartitem && cartitem.unitId) {
        // Product is in cart - use the cart item's unit
        activeUnit = product.units.find(u => u.unitId === cartitem.unitId) || product.units[0];
        activeUnitId = activeUnit.unitId;
    } else {
        // Product not in cart - use enriched product's active unit
        activeUnit = product.units.find(u => u.active) || product.units[0];
        activeUnitId = activeUnit.unitId;
    }

    /**
     * Increment product quantity by unit increment amount
     */
    const increment = () => {
        const qtty = currentQuantity + activeUnit.unitIncrement;
        updateDB(qtty);
    }
    
    /**
     * Decrement product quantity by unit increment amount
     */
    const decrement = () => {
        if (currentQuantity > 0) {
            const newQtty = Math.max(0, currentQuantity - activeUnit.unitIncrement);
            updateDB(newQtty);
        }
    }

    /**
     * Update product notes
     */
    const updateNotes = (e) => {
        updateDB(currentQuantity, activeUnitId, e.target.value);
    }

    /**
     * Handle unit switching (e.g., switching between kg and pieces)
     * Updates both visual display and cart data if product is in cart
     */
    const toggleUnit = (unitId) => {
        // Always notify parent component to update product display
        if (onUnitChange) {
            onUnitChange(unitId);
        }
        
        // If product is in cart, also update the cart via API
        if (currentQuantity > 0) {
            const unit = product.units.find(u => u.unitId === unitId);
            let adjustedQuantity = currentQuantity;
            
            // Ensure integer quantities for integer-increment units
            if (unit && Number.isInteger(unit.unitIncrement)) {   
                adjustedQuantity = parseInt(currentQuantity);
            }
            updateDB(adjustedQuantity, unitId, currentNotes);
        }
    }

    const toggleNotesInput = () => {
        setShowNotesInput(!showNotesInput);
    }

    /**
     * Update cart via API call with optimistic update.
     * Cart state is updated immediately so rapid clicks read the correct quantity;
     * the optimistic item is replaced with real server data on success, or reverted on failure.
     *
     * cartRef.current is updated synchronously (not via useEffect) so that a second click
     * that fires before React re-renders still sees the first click's optimistic cart.
     */
    async function updateDB(qtty, unitId = activeUnitId, notes = currentNotes) {
        if (qtty === 0 && currentQuantity === 0) return;

        const previousCart = cartRef.current;
        const existsInCart = previousCart.find((x) => x.productId === product.id);

        // Compute the optimistic cart synchronously
        let optimisticCart;
        if (existsInCart) {
            if (qtty === 0) {
                optimisticCart = previousCart.filter((x) => x.productId !== product.id);
            } else {
                optimisticCart = previousCart.map((x) =>
                    x.productId === product.id
                        ? { ...x, itemQtty: qtty, unitId, itemNotes: notes }
                        : x
                );
            }
        } else if (qtty > 0) {
            optimisticCart = [...previousCart, { id: null, productId: product.id, unitId, itemQtty: qtty, itemNotes: notes, product }];
        } else {
            return;
        }

        // Update ref synchronously before the await — the next rapid click reads this immediately
        cartRef.current = optimisticCart;
        setCart(optimisticCart);

        try {
            const apiUpdate = `${Constants.API_URL_UPDATE_CART}/${cartId}`;
            const data = { productId: product.id, unitId, itemQtty: qtty, notes };
            const response = await axios.post(apiUpdate, data, {
                headers: { 'Content-Type': 'application/json' },
                timeout: 10000,
                validateStatus: false
            });

            if (response.status >= 400) {
                cartRef.current = previousCart;
                setCart(previousCart);
                return;
            }

            if (qtty > 0) {
                // Replace optimistic item with confirmed server data (real id, server-computed fields)
                const confirmedCart = cartRef.current.map((x) =>
                    x.productId === response.data.productId ? response.data : x
                );
                cartRef.current = confirmedCart;
                setCart(confirmedCart);
            }
        } catch (error) {
            cartRef.current = previousCart;
            setCart(previousCart);
            console.error('Error updating cart:', error);
            console.error('Error response:', error.response?.data);
            console.error('Validation errors:', error.response?.data?.errors);
            if (error.response?.data?.errors?.['$']) {
                console.error('$ errors:', error.response.data.errors['$']);
            }
            if (error.response?.data?.errors?.updatedItem) {
                console.error('updatedItem errors:', error.response.data.errors.updatedItem);
            }
            console.error('Error status:', error.response?.status);
        }
    }

    // const Units = ({ units }) => (
    //     <div className="unit-types rounded-pill border border-secondary d-flex" style={{width: '120px', height: '30px'}}>
    //         {units.map((unit) => (
    //             <button 
    //                 key={unit.unitId}
    //                 className={`float-end rounded-pill units ${unit.active ? "active" : ""}`} // Use unit.active instead of comparing IDs
    //                 style={{width: `${unit.active ? '60%' : '40%'}`}} // Use unit.active instead of comparing IDs
    //                 onClick={() => toggleUnit(unit.unitId)}
    //             >
    //                 {unit.unitName}
    //             </button>
    //         ))}
    //     </div>
    // );

    /**
     * Unit selection buttons component
     * Displays buttons for switching between available units (e.g., kg/pieces)
     */
    const Units = ({ units }) => {
        return (
            <div className="unit-types  d-flex" style={UNIT_SELECTOR_STYLES.container}>
                {units.map((unit) => {
                    // Check if this unit is the currently active one (based on cart data or enriched product)
                    const isActive = unit.unitId === activeUnitId;
                    return (
                        <button 
                            key={unit.unitId}
                            className={`float-end units ${isActive ? "active" : ""}`}
                            style={isActive ? UNIT_SELECTOR_STYLES.activeButton : UNIT_SELECTOR_STYLES.inactiveButton}
                            onClick={() => toggleUnit(unit.unitId)}
                        >
                            {unit.unitName}
                        </button>
                    )
                })}
            </div>
        );
    };


    /**
     * Quantity increment/decrement controls
     */
    const QuantityControls = () => (
        <div className="action-buttons d-flex justify-content-center align-items-center">
            <button type="button" onClick={increment}>
                <i className="bi bi-plus-square" />
            </button>
            <input 
                onChange={() => null} 
                type="text" 
                className="product_qty" 
                value={currentQuantity} 
                readOnly
            />
            <button 
                type="button" 
                onClick={decrement}
                disabled={currentQuantity === 0}
                style={{opacity: currentQuantity === 0 ? 0.5 : 1}}
            >
                <i className="bi bi-dash-square" />
            </button>
        </div>
    );

    /**
     * Toggle button for showing/hiding notes input
     */
    const NotesToggleButton = () => (
        <button 
            className="notes-toggle-btn btn-sm p-2" 
            onClick={toggleNotesInput}
            style={{
                ...BUTTON_STYLES.notes,
                color: showNotesInput ? '#28a745' : '#6c757d'
            }}
            title={showNotesInput ? 'הסתר הערות' : 'הוסף הערות'}
        >
            <i className={`bi ${showNotesInput ? 'bi-chat-right-text-fill' : 'bi-chat-right-text'}`} style={{fontSize: ICON_SIZE}}></i>
        </button>
    );

    /**
     * Notes input field
     */
    const NotesInput = () => (
        <div className="ItemNotes w-100 mt-2">        
            <input 
                type="text" 
                className="form-control form-control-sm notes" 
                placeholder="הערות למוצר" 
                defaultValue={currentNotes}
                onBlur={updateNotes}
                style={{fontSize: NOTES_FONT_SIZE}}
            />
        </div>
    );

    /**
     * Remove item from cart button
     */
    const RemoveButton = () => (
        <button 
            className="remove-btn btn-sm p-2" 
            onClick={() => updateDB(0)}
            style={BUTTON_STYLES.remove}
            title="הסר מהסל"
        >
            <i className="bi bi-x-circle" style={{fontSize: ICON_SIZE}}></i>
        </button>
    );

    // Main component render
    return (
        <div ref={ref} className={`actions ${className} justify-content-evenly gap-2`}>
            {/* Product page & cart item layout */}
            {className.includes('product-page') 
            ? (
                <>
                    <QuantityControls />
                    {product.units.length > 1 && (
                        <Units units={product.units} />
                    )}
                    {className.includes('cart-item') && (
                        <>
                            <NotesToggleButton />
                            <RemoveButton />
                        </>
                    )}
                    {(showNotes || showNotesInput) && ( 
                        <NotesInput />
                    )}
                </>
            ) : (
                <>
                    {/* Desktop/Large screen layout */}
                    <div className="w-100 d-md-flex d-none flex-column justify-content-evenly align-items-center flex-wrap h-100">
                        <QuantityControls />
                        {product.units.length > 1 && (
                            <Units units={product.units} />
                        )}
                    </div>
                    
                    {/* Mobile/Small screen layout */}
                    <div className="w-100 d-md-none d-block">
                        <div className="d-flex justify-content-center align-content-around flex-wrap">
                            <div className="mb-3">
                                <QuantityControls />
                            </div>
                            {product.units.length > 1 && (
                                <div>
                                    <Units units={product.units} />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Notes input appears below when toggled */}
                    {showNotesInput && (
                        <NotesInput />
                    )}
                </>
            )}
        </div>
    );
});

export default ProductActions;